import React, { useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import { apiGetJson, apiPutJson } from "../utils/apiClient";
import LoadingInline from "../components/LoadingInline";
import { ToastViewport, useToast } from "../components/Toast";
import { useUserContext } from "../contexts/UserContext";
import { HelpTip } from "../components/HelpTip";
import { SupportAnchorBanner } from "../components/SupportAnchorBanner";
import { FixedCostSupportInfo } from "../components/FixedCostSupportInfo";

/** ★ apiClient と同じ判定（exportされてないのでページ側に最小実装） */
function isDemoModeLocal(): boolean {
    if (typeof window === "undefined") return false;
    if (window.location.pathname.startsWith("/demo")) return true;
    const ls = localStorage.getItem("demoMode");
    return ls === "1" || ls === "true";
}

const pad2 = (n: number) => String(n).padStart(2, "0");
function clamp(n: number, min: number, max: number) {
    return Math.min(Math.max(n, min), max);
}
function normalizeStartMonth(v: any): number {
    const n = Number(v);
    if (!Number.isFinite(n)) return 1;
    return clamp(Math.trunc(n), 1, 12);
}

/** ✅ “今” と 期首月から、デフォルトで表示すべき年度(=開始年) を決める */
function computeDefaultYearForStartMonth(now: dayjs.Dayjs, startMonth: number) {
    const sm = normalizeStartMonth(startMonth);
    const startThisYear = dayjs(`${now.year()}-${pad2(sm)}-01`);
    return now.isBefore(startThisYear) ? now.year() - 1 : now.year();
}

/** 税込（あなたの固定費一覧に合わせる） */
const SHOP_PLANS_TAX_IN = [
    { label: "がんばれ", fee: 27500 },
    { label: "スタンダード", fee: 71500 },
    { label: "メガショップ", fee: 143000 },
] as const;

const RSNS_YEN = 3300;
const RMS_BATCH_EDIT_YEN = 11000;

const RCABINET_OPTIONS = [
    { value: "", label: "なし", fee: 0, name: "" },
    {
        value: "1GB",
        label: "1GB追加プラン",
        fee: 5000,
        name: "R-Cabinet 容量追加オプション_1GB追加プラン",
    },
    {
        value: "10GB",
        label: "10GB追加プラン",
        fee: 10000,
        name: "R-Cabinet 容量追加オプション_10GB追加プラン",
    },
] as const;

const LINE_PLAN_OPTIONS = [
    { value: "", label: "なし", fee: 0 },
    { value: "ライト", label: "ライトプラン", fee: 5500 },
    { value: "スタンダード", label: "スタンダードプラン", fee: 16500 },
] as const;

const RAKURAKU_COUPON_YEN = 11000;

const ECMASTERS_OPTIONS = [
    { value: "", label: "なし", fee: 0, name: "" },
    {
        value: "スターター",
        label: "スタータープラン",
        fee: 16500,
        name: "外部サービス_ECマスターズクラブ_スタータープラン",
    },
    {
        value: "ベーシック",
        label: "ベーシックプラン",
        fee: 22000,
        name: "外部サービス_ECマスターズクラブ_ベーシックプラン",
    },
    {
        value: "アドバンス",
        label: "アドバンスプラン",
        fee: 33000,
        name: "外部サービス_ECマスターズクラブ_アドバンスプラン",
    },
    {
        value: "アンリミテッド",
        label: "アンリミテッドプラン",
        fee: 66000,
        name: "外部サービス_ECマスターズクラブ_アンリミテッドプラン",
    },
] as const;

type MonthForm = any;
type ApplyScope = "all" | "from" | "range";

/** ✅ 固定サービス・自由入力の「DB項目名」をここに集約 */
const FIELDS = {
    RCABINET: { name: "rcabinet_plan", cost: "rcabinet_cost" },
    RAKURAKU: { name: "rakuraku_coupon_plan", cost: "rakuraku_coupon_cost" },
    ECMASTERS: { name: "ecmasters_plan", cost: "ecmasters_cost" },

    FREE1: { name: "paid_system_1_name", cost: "paid_system_1_cost" },
    FREE2: { name: "paid_system_2_name", cost: "paid_system_2_cost" },
    FREE3: { name: "paid_system_3_name", cost: "paid_system_3_cost" },
    FREE4: { name: "paid_system_4_name", cost: "paid_system_4_cost" },
    FREE5: { name: "paid_system_5_name", cost: "paid_system_5_cost" },
    FREE6: { name: "paid_system_6_name", cost: "paid_system_6_cost" },
    FREE7: { name: "paid_system_7_name", cost: "paid_system_7_cost" },

    OTHER: { cost: "other_fixed_cost" },
} as const;

const FREE_ROWS = [
    { key: "FREE1", label: "自由入力①", ex: "" },
    { key: "FREE2", label: "自由入力②", ex: "" },
    { key: "FREE3", label: "自由入力③", ex: "" },
    { key: "FREE4", label: "自由入力④", ex: "" },
    { key: "FREE5", label: "自由入力⑤", ex: "" },
    { key: "FREE6", label: "自由入力⑥", ex: "" },
    { key: "FREE7", label: "自由入力⑦", ex: "" },
] as const;

type TemplateFreeRowDraft = { name: string; cost: string };

function buildEmptyTemplateFreeRows(): Record<string, TemplateFreeRowDraft> {
    return Object.fromEntries(FREE_ROWS.map((r) => [r.key, { name: "", cost: "" }])) as Record<
        string,
        TemplateFreeRowDraft
    >;
}

function parseTemplateMoney(raw: string) {
    const s = String(raw ?? "").trim();
    if (s === "") return null;
    if (!/^-?\d+$/.test(s)) return null;
    const n = Number(s);
    return Number.isSafeInteger(n) ? n : null;
}

/** ✅ 左列固定（sticky） */
const LEFT_W = "w-[320px] min-w-[320px] max-w-[320px]";
const STICKY_TH = `sticky left-0 z-40 bg-gray-100 ${LEFT_W}`;
const STICKY_TD = `sticky left-0 z-30 bg-white ${LEFT_W}`;
const STICKY_TD_GRAY = `sticky left-0 z-30 bg-gray-50 ${LEFT_W}`;

/** ✅ 全体テーブル */
const TABLE_CLASS = "min-w-[1600px] w-full text-sm border-separate border-spacing-0";

/** ✅ “詰める”ための共通クラス（漏れをなくす） */
const TH_CELL = "p-1 border whitespace-nowrap bg-gray-100";
const TD_CELL = "p-0.5 border bg-white align-top";
const TD_CELL_GRAY = "p-0.5 border bg-gray-50 align-top";

const UI_SELECT_CELL = "w-full border px-1 py-0 text-xs leading-none h-7 bg-white";
const UI_INPUT_NUM_CELL = "w-full border px-1 py-0 text-xs leading-none h-7 text-right";
const UI_INPUT_TEXT_CELL = "w-full border px-1 py-0 text-xs leading-none h-7 bg-white";
const UI_SUB_AMOUNT = "text-right text-[10px] text-gray-600 mt-0.5 leading-none";

const UI_SELECT_CTRL = "border px-2 py-0.5 rounded bg-white text-sm h-7";
const UI_BTN_SM = "px-3 py-1 border bg-white rounded hover:bg-gray-50 disabled:opacity-60";
const UI_BTN_PRIMARY =
    "px-4 py-1.5 bg-blue-600 text-white rounded shadow-sm hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed";

function LeftCell({
    children,
    gray = false,
    className = "",
}: {
    children: React.ReactNode;
    gray?: boolean;
    className?: string;
}) {
    return (
        <td className={`p-1 border font-medium ${gray ? STICKY_TD_GRAY : STICKY_TD} ${className}`}>
            {children}
        </td>
    );
}

/** ✅ 期首月を取得する設定API（あなたの実装に合わせて変更） */
const SETTINGS_ENDPOINT = "/api/company/settings/fiscal";

async function fetchFiscalStartMonth(shopUrl: string): Promise<number | null> {
    const d = await apiGetJson<any>(`${SETTINGS_ENDPOINT}?shop_url=${encodeURIComponent(shopUrl)}`);
    const m = d?.fiscalMonth ?? null;
    return m == null ? null : Number(m);
}


export default function FiscalRakutenExpensePage({ shopUrl }: { shopUrl: string }) {
    //console.log("1_shopUrl=" + shopUrl);

    const user = useUserContext();

    const isDemo = useMemo(() => isDemoModeLocal(), []);

    // ✅ 追加：複数月一括適用機能開閉
    const [templateOpen, setTemplateOpen] = useState(false);

    // ✅ shopUrl が空で encodeURIComponent に入ると落ちる/変なAPIになるのでガード
    const shopUrlSafe = useMemo(() => {
        const trimmed = String(shopUrl ?? "").trim();
        if (trimmed) return trimmed;
        return isDemo ? "demo-shop" : "";
    }, [shopUrl, isDemo]);


    const missingShopUrl = !isDemo && !shopUrlSafe;

    /** ✅ 設定ロード */
    const [settingsLoading, setSettingsLoading] = useState(true);
    const [startMonth, setStartMonth] = useState<number>(1);

    /** ✅ 年度（＝開始年） */
    const userChangedYearRef = useRef(false);
    const [year, setYear] = useState<number>(() => computeDefaultYearForStartMonth(dayjs(), 1));

    /**
     * ✅ 追加：shopUrl 切替時のレース対策（古いロード結果を捨てる）
     * - loadToken が一致する時だけ state へ反映
     */
    const loadTokenRef = useRef(0);

    /** ✅ 追加：未保存トースト */
    const [dirty, setDirty] = useState(false);
    const [dirtyHintDismissed, setDirtyHintDismissed] = useState(false);
    useEffect(() => {
        if (dirty) setDirtyHintDismissed(false);
    }, [dirty]);

    /** ✅ 未保存ガード：タブ閉じ/リロード時にブラウザ標準の確認を出す */
    useEffect(() => {
        if (!dirty) return;
        const handler = (e: BeforeUnloadEvent) => {
            e.preventDefault();
            e.returnValue = "";
        };
        window.addEventListener("beforeunload", handler);
        return () => window.removeEventListener("beforeunload", handler);
    }, [dirty]);

    /** ✅ 未保存ガード：年度切替の前に確認 */
    const confirmDiscardIfDirty = () => {
        if (!dirty) return true;
        return window.confirm("未保存の変更があります。\n移動すると入力内容は破棄されます。よろしいですか？");
    };

    /** ✅ トースト本体 */
    const { toasts, pushToast, removeToast } = useToast();

    /**
     * ✅ 追加：shop 切替のたびに状態をリセット＋「年度自動」を許可
     *   （店ごとに期首月が違うので、自然に合う年度へ戻したい）
     */
    useEffect(() => {
        loadTokenRef.current++;
        setDataByMonth({});
        setError(null);
        userChangedYearRef.current = false;

        // ✅ 未保存状態もリセット
        setDirty(false);
        setDirtyHintDismissed(false);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shopUrlSafe]);

    useEffect(() => {
        let cancelled = false;

        const run = async () => {
            setSettingsLoading(true);

            try {
                if (isDemo) {
                    const sm = 1;
                    if (cancelled) return;
                    setStartMonth(sm);
                    setYear(computeDefaultYearForStartMonth(dayjs(), sm));
                    return;
                }

                if (!shopUrlSafe) {
                    // shopUrl 未指定は「設定取得不可」なので 1月期のまま
                    if (!cancelled) {
                        setStartMonth(1);
                        if (!userChangedYearRef.current) setYear(computeDefaultYearForStartMonth(dayjs(), 1));
                    }
                    return;
                }

                const raw = await fetchFiscalStartMonth(shopUrlSafe);
                const sm = normalizeStartMonth(raw ?? 1);

                if (cancelled) return;

                setStartMonth(sm);

                if (!userChangedYearRef.current) {
                    setYear(computeDefaultYearForStartMonth(dayjs(), sm));
                }
            } catch {
                if (!cancelled) {
                    setStartMonth(1);
                    if (!userChangedYearRef.current) setYear(computeDefaultYearForStartMonth(dayjs(), 1));
                }
            } finally {
                if (!cancelled) setSettingsLoading(false);
            }
        };

        run();
        return () => {
            cancelled = true;
        };
    }, [shopUrlSafe, isDemo]);

    /** ✅ 12ヶ月を “年度(開始年) + 期首月” で作る（settingsLoadingでも安全に作れる） */
    const months = useMemo(() => {
        const start = dayjs(`${year}-${pad2(startMonth)}-01`);
        return Array.from({ length: 12 }, (_, i) => start.add(i, "month"));
    }, [year, startMonth]);

    const monthKeyList = useMemo(() => months.map((m) => m.format("YYYY-MM")), [months]);

    const periodLabel = useMemo(() => {
        const s = months[0];
        const e = months[months.length - 1];
        if (!s || !e) return "";
        return `${s.format("YYYY/MM")}〜${e.format("YYYY/MM")}`;
    }, [months]);

    /** ✅ 年度候補 */
    const baseYear = useMemo(() => computeDefaultYearForStartMonth(dayjs(), startMonth), [startMonth]);
    const yearOptions = useMemo(() => Array.from({ length: 6 }, (_, i) => baseYear - 2 + i), [baseYear]);

    // ---------------------
    // data model
    // ---------------------
    const emptyForm: MonthForm = useMemo(
        () => ({
            shop_plan: "",
            shop_fee: null,

            rsns: null,
            rms_batch_edit: null,

            line_plan_cost: null,

            [FIELDS.RCABINET.name]: "",
            [FIELDS.RCABINET.cost]: null,
            [FIELDS.RAKURAKU.name]: "",
            [FIELDS.RAKURAKU.cost]: null,
            [FIELDS.ECMASTERS.name]: "",
            [FIELDS.ECMASTERS.cost]: null,

            [FIELDS.FREE1.name]: "",
            [FIELDS.FREE1.cost]: null,
            [FIELDS.FREE2.name]: "",
            [FIELDS.FREE2.cost]: null,
            [FIELDS.FREE3.name]: "",
            [FIELDS.FREE3.cost]: null,
            [FIELDS.FREE4.name]: "",
            [FIELDS.FREE4.cost]: null,
            [FIELDS.FREE5.name]: "",
            [FIELDS.FREE5.cost]: null,
            [FIELDS.FREE6.name]: "",
            [FIELDS.FREE6.cost]: null,
            [FIELDS.FREE7.name]: "",
            [FIELDS.FREE7.cost]: null,

            [FIELDS.OTHER.cost]: null,
        }),
        []
    );

    const [dataByMonth, setDataByMonth] = useState<Record<string, MonthForm>>({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const savingRef = useRef(false);

    const updateMonthField = (ym: string, field: string, value: any) => {
        setDirty(true);
        setDataByMonth((prev) => ({
            ...prev,
            [ym]: {
                ...(prev[ym] || { ...emptyForm }),
                [field]: value,
            },
        }));
    };

    // ✅ 追加：INT64想定フィールド用（小数点/指数表記などを弾く）
    function parseIntOrNullStrict(raw: string) {
        const s = String(raw ?? "").trim();
        if (s === "") return { ok: true as const, value: null as number | null };

        // number input は "12." や "1e3" 等も来るので、数字だけ許可
        if (!/^\d+$/.test(s)) {
            return { ok: false as const, reason: "整数のみ入力できます（小数点は不可）" };
        }

        const n = Number(s);
        if (!Number.isSafeInteger(n)) {
            return { ok: false as const, reason: "数値が大きすぎます" };
        }
        return { ok: true as const, value: n };
    }

    function handleIntChange(ym: string, field: string, raw: string) {
        const r = parseIntOrNullStrict(raw);
        if (!r.ok) {
            pushToast("error", r.reason);
            return; // ✅ state更新しない = 保存もされない
        }
        updateMonthField(ym, field, r.value);
    }

    const applyToMonths = (yms: string[], updater: (cur: MonthForm, ym: string) => MonthForm) => {
        setDirty(true);
        setDataByMonth((prev) => {
            const next: Record<string, MonthForm> = { ...prev };
            for (const ym of yms) {
                const cur = prev[ym] || { ...emptyForm };
                next[ym] = updater(cur, ym);
            }
            return next;
        });
    };

    const planFeeFor = (label: string) => SHOP_PLANS_TAX_IN.find((p) => p.label === label)?.fee ?? null;

    const setShopPlanMonth = (ym: string, label: string) => {
        setDirty(true);
        const fee = planFeeFor(label);
        setDataByMonth((prev) => ({
            ...prev,
            [ym]: {
                ...(prev[ym] || { ...emptyForm }),
                shop_plan: label,
                shop_fee: fee,
            },
        }));
    };

    const toggleFixedAmountMonth = (ym: string, field: string, enabled: boolean, amount: number) => {
        updateMonthField(ym, field, enabled ? amount : null);
    };

    const setRcabinetMonth = (ym: string, v: string) => {
        setDirty(true);
        const opt = RCABINET_OPTIONS.find((x) => x.value === v);
        setDataByMonth((prev) => ({
            ...prev,
            [ym]: {
                ...(prev[ym] || { ...emptyForm }),
                [FIELDS.RCABINET.name]: opt?.name || "",
                [FIELDS.RCABINET.cost]: opt && opt.fee > 0 ? opt.fee : null,
            },
        }));
    };

    const setLinePlanMonth = (ym: string, v: string) => {
        const opt = LINE_PLAN_OPTIONS.find((x) => x.value === v);
        updateMonthField(ym, "line_plan_cost", opt && opt.fee > 0 ? opt.fee : null);
    };

    const toggleRakurakuCouponMonth = (ym: string, enabled: boolean) => {
        setDirty(true);
        setDataByMonth((prev) => ({
            ...prev,
            [ym]: {
                ...(prev[ym] || { ...emptyForm }),
                [FIELDS.RAKURAKU.name]: enabled ? "外部サービス_らくらくーぽん" : "",
                [FIELDS.RAKURAKU.cost]: enabled ? RAKURAKU_COUPON_YEN : null,
            },
        }));
    };

    const setEcmastersMonth = (ym: string, v: string) => {
        setDirty(true);
        const opt = ECMASTERS_OPTIONS.find((x) => x.value === v);
        setDataByMonth((prev) => ({
            ...prev,
            [ym]: {
                ...(prev[ym] || { ...emptyForm }),
                [FIELDS.ECMASTERS.name]: opt?.name || "",
                [FIELDS.ECMASTERS.cost]: opt && opt.fee > 0 ? opt.fee : null,
            },
        }));
    };

    const detectRcabinetValue = (ym: string) => {
        const name = String(dataByMonth[ym]?.[FIELDS.RCABINET.name] || "");
        if (name.includes("10GB")) return "10GB";
        if (name.includes("1GB")) return "1GB";
        return "";
    };

    const detectLineValue = (ym: string) => {
        const v = Number(dataByMonth[ym]?.line_plan_cost);
        if (v === 5500) return "ライト";
        if (v === 16500) return "スタンダード";
        return "";
    };

    const detectEcmastersValue = (ym: string) => {
        const name = String(dataByMonth[ym]?.[FIELDS.ECMASTERS.name] || "");
        if (name.includes("アンリミテッド")) return "アンリミテッド";
        if (name.includes("アドバンス")) return "アドバンス";
        if (name.includes("ベーシック")) return "ベーシック";
        if (name.includes("スターター")) return "スターター";
        return "";
    };

    // ---------------------
    // 範囲適用（複数月一括適用機能）
    // ---------------------
    const [applyScope, setApplyScope] = useState<ApplyScope>("all");
    const [applyFromYm, setApplyFromYm] = useState<string>(monthKeyList[0] || "");
    const [applyToYm, setApplyToYm] = useState<string>(monthKeyList[monthKeyList.length - 1] || "");

    useEffect(() => {
        setApplyFromYm(monthKeyList[0] || "");
        setApplyToYm(monthKeyList[monthKeyList.length - 1] || "");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [monthKeyList.join("|")]);

    const resolveTargetMonths = () => {
        if (monthKeyList.length === 0) return [];
        if (applyScope === "all") return [...monthKeyList];

        const fromIdx = clamp(monthKeyList.indexOf(applyFromYm), 0, monthKeyList.length - 1);
        if (applyScope === "from") return monthKeyList.slice(fromIdx);

        const toIdxRaw = monthKeyList.indexOf(applyToYm);
        const toIdx = clamp(toIdxRaw < 0 ? monthKeyList.length - 1 : toIdxRaw, 0, monthKeyList.length - 1);
        const a = Math.min(fromIdx, toIdx);
        const b = Math.max(fromIdx, toIdx);
        return monthKeyList.slice(a, b + 1);
    };

    const [tplShopPlan, setTplShopPlan] = useState<string>("");
    const [tplRsns, setTplRsns] = useState<boolean>(false);
    const [tplRmsBatchEdit, setTplRmsBatchEdit] = useState<boolean>(false);
    const [tplRcabinet, setTplRcabinet] = useState<string>("");
    const [tplLinePlan, setTplLinePlan] = useState<string>("");
    const [tplRakurakuCoupon, setTplRakurakuCoupon] = useState<boolean>(false);
    const [tplEcmasters, setTplEcmasters] = useState<string>("");
    const [tplOtherCost, setTplOtherCost] = useState<string>("");
    const [tplFreeRows, setTplFreeRows] = useState<Record<string, TemplateFreeRowDraft>>(() =>
        buildEmptyTemplateFreeRows()
    );

    const applyTemplateToRange = () => {
        const targets = resolveTargetMonths();
        if (targets.length === 0) return;

        applyToMonths(targets, (cur) => {
            const next = { ...cur };

            if (tplShopPlan !== "") {
                next.shop_plan = tplShopPlan;
                next.shop_fee = planFeeFor(tplShopPlan);
            }

            next.rsns = tplRsns ? RSNS_YEN : null;
            next.rms_batch_edit = tplRmsBatchEdit ? RMS_BATCH_EDIT_YEN : null;

            {
                const opt = RCABINET_OPTIONS.find((x) => x.value === tplRcabinet);
                next[FIELDS.RCABINET.name] = opt?.name || "";
                next[FIELDS.RCABINET.cost] = opt && opt.fee > 0 ? opt.fee : null;
            }

            {
                const opt = LINE_PLAN_OPTIONS.find((x) => x.value === tplLinePlan);
                next.line_plan_cost = opt && opt.fee > 0 ? opt.fee : null;
            }

            next[FIELDS.RAKURAKU.name] = tplRakurakuCoupon ? "外部サービス_らくらくーぽん" : "";
            next[FIELDS.RAKURAKU.cost] = tplRakurakuCoupon ? RAKURAKU_COUPON_YEN : null;

            {
                const opt = ECMASTERS_OPTIONS.find((x) => x.value === tplEcmasters);
                next[FIELDS.ECMASTERS.name] = opt?.name || "";
                next[FIELDS.ECMASTERS.cost] = opt && opt.fee > 0 ? opt.fee : null;
            }

            for (const row of FREE_ROWS) {
                const draft = tplFreeRows[row.key] || { name: "", cost: "" };
                const map = (FIELDS as any)[row.key] as { name: string; cost: string };
                next[map.name] = draft.name;
                next[map.cost] = parseTemplateMoney(draft.cost);
            }

            next[FIELDS.OTHER.cost] = parseTemplateMoney(tplOtherCost);

            return next;
        });

        pushToast("success", `複数月一括適用を ${targets.length} ヶ月に適用しました`);
    };

    // ---------------------
    // 全項目が空かどうかの判定
    // ---------------------
    const isFormEmpty = (m: MonthForm): boolean => {
        const numFields = [
            "shop_fee", "rsns", "rms_batch_edit", "line_plan_cost",
            FIELDS.RCABINET.cost, FIELDS.RAKURAKU.cost, FIELDS.ECMASTERS.cost,
            FIELDS.FREE1.cost, FIELDS.FREE2.cost, FIELDS.FREE3.cost,
            FIELDS.FREE4.cost, FIELDS.FREE5.cost, FIELDS.FREE6.cost, FIELDS.FREE7.cost,
            FIELDS.OTHER.cost,
        ];
        const strFields = [
            "shop_plan",
            FIELDS.RCABINET.name, FIELDS.RAKURAKU.name, FIELDS.ECMASTERS.name,
            FIELDS.FREE1.name, FIELDS.FREE2.name, FIELDS.FREE3.name,
            FIELDS.FREE4.name, FIELDS.FREE5.name, FIELDS.FREE6.name, FIELDS.FREE7.name,
        ];
        const hasNum = numFields.some((k) => m?.[k] != null);
        const hasStr = strFields.some((k) => String(m?.[k] ?? "").trim() !== "");
        return !hasNum && !hasStr;
    };

    // ---------------------
    // load / save
    // ---------------------
    const loadAll = async () => {
        if (missingShopUrl) {
            setError("shopUrl が未指定です（このページは shopUrl が必要です）");
            pushToast("error", "shopUrl が未指定です");
            return;
        }

        // ✅ このロードの「世代」と「参照値」を固定して、shop 切替のレースを防ぐ
        const token = ++loadTokenRef.current;
        const currentShopUrl = shopUrlSafe;
        const currentMonths = [...monthKeyList];

        setLoading(true);
        setError(null);

        try {
            const pairs = await Promise.all(
                currentMonths.map(async (ym) => {
                    try {
                        const d = await apiGetJson<any>(
                            `/api/rakuten-expense?shop_url=${encodeURIComponent(currentShopUrl)}&year_month=${ym}`
                        );

                        //console.log("loaded", ym, d);

                        return [ym, d ? { ...emptyForm, ...d } : { ...emptyForm }] as const;
                    } catch (e: any) {
                        console.warn("[FiscalRakutenExpensePage] load failed:", ym, e);
                        return [ym, { ...emptyForm }] as const;
                    }
                })
            );

            // ✅ shop 切替などで token が進んだら、この結果は捨てる
            if (token !== loadTokenRef.current) return;

            const next: Record<string, MonthForm> = {};
            for (const [ym, form] of pairs) next[ym] = form;
            setDataByMonth(next);

            // ✅ ロードしたら未保存解除
            setDirty(false);
            setDirtyHintDismissed(false);
        } catch (e: any) {
            if (token !== loadTokenRef.current) return;
            setError(e?.message || "load error");
            pushToast("error", e?.message || "読み込みに失敗しました");
            setDataByMonth({});
        } finally {
            setLoading(false);
        }
    };

    /** ✅ 期首月が確定してからロード（settingsLoadingが落ち着いてから） */
    useEffect(() => {
        if (settingsLoading) return;
        if (missingShopUrl) return; // shopUrl なしはロードしない（白画面回避）
        loadAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [settingsLoading, shopUrlSafe, monthKeyList.join("|")]);

    const handleSaveAll = async () => {
        if (missingShopUrl) {
            pushToast("error", "shopUrl が未指定です（保存できません）");
            return;
        }

        if (savingRef.current) return;
        savingRef.current = true;
        setSaving(true);

        // ✅ 保存開始時点の shopUrl / months を固定（途中切替で別店舗に書かない）
        const token = ++loadTokenRef.current;
        const currentShopUrl = shopUrlSafe;
        const currentMonths = [...monthKeyList];

        try {
            // RN-246: 12か月分を1回でまとめて保存する（1か月ずつ12本同時だと、書き込みの順番待ちで10〜18秒かかっていた）
            const fixedMonths = currentMonths
                // 全項目が空の月はDB行を作らずスキップ
                .filter((ym) => !isFormEmpty(dataByMonth[ym] || { ...emptyForm }))
                .map((ym) => {
                    const m = dataByMonth[ym] || { ...emptyForm };
                    const ymDate = `${ym}-01`;
                    const { courier_shipping_cost: _c, mail_shipping_cost: _ma, logistics_cost: _l, ...fixedFields } = m;
                    return {
                        year_month: ymDate,
                        ...fixedFields,
                    };
                });
            if (fixedMonths.length > 0) {
                await apiPutJson("/api/rakuten-expense/months", {
                    shop_url: currentShopUrl,
                    months: fixedMonths,
                });
            }

            if (token !== loadTokenRef.current) return;

            pushToast("success", "12ヶ月分を保存しました");

            // ✅ 保存成功で未保存解除
            setDirty(false);
            setDirtyHintDismissed(false);

            loadAll();
        } catch (e: any) {
            if (token !== loadTokenRef.current) return;
            console.warn("[FiscalRakutenExpensePage] save failed:", e);
            pushToast("error", e?.message || "保存に失敗しました");
        } finally {
            savingRef.current = false;
            setSaving(false);
        }
    };

    // ---------------------
    // 集計
    // ---------------------
    const calcFixedTotal = (form: MonthForm) => {
        const fields = [
            "shop_fee",
            "rsns",
            "rms_batch_edit",
            "line_plan_cost",
            FIELDS.RCABINET.cost,
            FIELDS.RAKURAKU.cost,
            FIELDS.ECMASTERS.cost,

            FIELDS.FREE1.cost,
            FIELDS.FREE2.cost,
            FIELDS.FREE3.cost,
            FIELDS.FREE4.cost,
            FIELDS.FREE5.cost,
            FIELDS.FREE6.cost,
            FIELDS.FREE7.cost,

            FIELDS.OTHER.cost,
        ];
        return fields.reduce((sum, key) => {
            const v = Number(form?.[key]);
            return sum + (isNaN(v) ? 0 : v);
        }, 0);
    };

    const totalsByMonth = useMemo(() => {
        const out: Record<string, number> = {};
        for (const ym of monthKeyList) out[ym] = calcFixedTotal(dataByMonth[ym] || emptyForm);
        return out;
    }, [dataByMonth, monthKeyList.join("|"), emptyForm]);

    // ✅ ここが Dashboard と同じ「overlay」制御
    const overlayBusy = settingsLoading || (loading && Object.keys(dataByMonth).length === 0);

    return (
        <div className="relative">
            {/* ✅ トースト */}
            <ToastViewport toasts={toasts} onClose={removeToast}>
                {/* 未保存ヒント（閉じるまで常駐） */}
                {dirty && !dirtyHintDismissed && !saving && (
                    <div className="pointer-events-auto">
                        <div className="flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 shadow">
                            <div className="text-xs text-amber-900">変更があります。右上の「12ヶ月分を保存」を押してください。</div>
                            <button
                                className="ml-2 text-xs px-2 py-1 rounded border bg-white hover:bg-gray-50"
                                onClick={() => setDirtyHintDismissed(true)}
                                aria-label="閉じる"
                            >
                                ×
                            </button>
                        </div>
                    </div>
                )}
            </ToastViewport>

            {/* ✅ overlay ローディング（白画面にならない） */}
            {overlayBusy && (
                <div className="fixed top-14 bottom-0 left-0 md:left-[210px] right-0 z-50 flex items-center justify-center bg-white/0">
                    <LoadingInline />
                </div>
            )}

            <div className="max-w-full">
                {/* shopUrl が無い場合も枠は出す（白画面回避） */}
                {missingShopUrl && (
                    <div className="mb-2 text-sm text-red-600">shopUrl が未指定です（このページは shopUrl が必要です）</div>
                )}

                <SupportAnchorBanner sectionId="fixed-cost-support-section" />

                {/* 上部コントロール */}
                <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                    <div className="flex items-center gap-2 flex-wrap">
                        <div className="text-lg font-semibold">固定費（年度 / 12ヶ月）</div>

                        <label className="text-sm text-gray-600 ml-2">年度</label>
                        <select
                            className={UI_SELECT_CTRL}
                            value={year}
                            onChange={(e) => {
                                if (!confirmDiscardIfDirty()) return;
                                userChangedYearRef.current = true;
                                setYear(Number(e.target.value));
                            }}
                            disabled={settingsLoading}
                        >
                            {yearOptions.map((y) => (
                                <option key={y} value={y}>
                                    {y}年度
                                </option>
                            ))}
                        </select>

                        <div className="text-sm text-gray-600 ml-2">
                            期首月: {startMonth}月 / 表示期間: {periodLabel}
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => {
                                if (!confirmDiscardIfDirty()) return;
                                loadAll();
                            }}
                            disabled={loading || missingShopUrl}
                            className={UI_BTN_SM}
                        >
                            再読込
                        </button>

                        <button
                            onClick={handleSaveAll}
                            disabled={saving || missingShopUrl}
                            className={`${UI_BTN_PRIMARY} ${dirty ? "ring-2 ring-amber-400" : ""}`}
                        >
                            {saving ? "保存中..." : "12ヶ月分を保存"}
                        </button>
                    </div>
                </div>

                {error && <div className="mb-2 text-sm text-red-600">{error}</div>}

                {/* 複数月の固定費を一括で変更＋範囲適用 */}
                <div className="mb-3 p-2 border rounded bg-gray-50">
                    {/* ✅ ヘッダ：折りたたみ + HelpTip */}
                    <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                            <div className="font-semibold">複数月の固定費を一括で変更（必要な範囲に適用）</div>

                            {/* ✅ 追加：？HelpTip */}
                            <HelpTip
                                text={
                                    <div className="text-xs leading-relaxed">
                                        初回入力時に複数の月にデータを登録したい場合にご活用ください。
                                    </div>
                                }
                            />
                        </div>

                        <button
                            type="button"
                            className="h-8 w-8 inline-flex items-center justify-center rounded border bg-white hover:bg-gray-50"
                            onClick={() => setTemplateOpen((v) => !v)}
                            aria-expanded={templateOpen}
                            aria-label={templateOpen ? "複数月一括適用機能を折りたたむ" : "複数月一括適用機能を開く"}
                            title={templateOpen ? "折りたたむ" : "開く"}
                        >
                            <span className={`transition-transform ${templateOpen ? "rotate-180" : "rotate-0"}`}>
                                ▼
                            </span>
                        </button>
                    </div>

                    {/* ✅ 中身：折りたたみ */}
                    {templateOpen && (
                        <>
                            <div className="flex flex-wrap items-center gap-2 mb-2 mt-2">
                                <label className="text-sm text-gray-700">適用範囲</label>
                                <select
                                    className={UI_SELECT_CTRL}
                                    value={applyScope}
                                    onChange={(e) => setApplyScope(e.target.value as ApplyScope)}
                                >
                                    <option value="all">全月</option>
                                    <option value="from">この月以降</option>
                                    <option value="range">範囲</option>
                                </select>

                                <select
                                    className={UI_SELECT_CTRL}
                                    value={applyFromYm}
                                    onChange={(e) => setApplyFromYm(e.target.value)}
                                >
                                    {monthKeyList.map((ym) => (
                                        <option key={ym} value={ym}>
                                            {dayjs(`${ym}-01`).format("YYYY/MM")}
                                        </option>
                                    ))}
                                </select>

                                {applyScope === "range" && (
                                    <>
                                        <span className="text-sm text-gray-500">〜</span>
                                        <select
                                            className={UI_SELECT_CTRL}
                                            value={applyToYm}
                                            onChange={(e) => setApplyToYm(e.target.value)}
                                        >
                                            {monthKeyList.map((ym) => (
                                                <option key={ym} value={ym}>
                                                    {dayjs(`${ym}-01`).format("YYYY/MM")}
                                                </option>
                                            ))}
                                        </select>
                                    </>
                                )}
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
                                {/* ここから下は “既存の複数月一括適用機能中身” をそのままコピペでOK */}
                                <div>
                                    <label className="text-sm text-gray-600">楽天出店プラン（途中変更OK）</label>
                                    <select
                                        className="w-full border px-2 mt-1 bg-white text-sm h-7"
                                        value={tplShopPlan}
                                        onChange={(e) => setTplShopPlan(e.target.value)}
                                    >
                                        <option value="">（この項目は変更しない）</option>
                                        {SHOP_PLANS_TAX_IN.map((p) => (
                                            <option key={p.label} value={p.label}>
                                                {p.label}（{p.fee.toLocaleString()}円）
                                            </option>
                                        ))}
                                    </select>
                                    <div className="text-xs text-gray-500 mt-1">※空なら「プランは触らない」</div>
                                </div>

                                <label className="flex items-center gap-1 text-sm mt-3">
                                    <input
                                        type="checkbox"
                                        checked={tplRsns}
                                        onChange={(e) => setTplRsns(e.target.checked)}
                                    />
                                    R-SNS（{RSNS_YEN.toLocaleString()}円）ON
                                </label>

                                <label className="flex items-center gap-1 text-sm mt-3">
                                    <input
                                        type="checkbox"
                                        checked={tplRmsBatchEdit}
                                        onChange={(e) => setTplRmsBatchEdit(e.target.checked)}
                                    />
                                    RMS商品一括編集（{RMS_BATCH_EDIT_YEN.toLocaleString()}円）ON
                                </label>

                                <div>
                                    <label className="text-sm text-gray-600">R-Cabinet 容量追加</label>
                                    <select
                                        className="w-full border px-2 mt-1 bg-white text-sm h-7"
                                        value={tplRcabinet}
                                        onChange={(e) => setTplRcabinet(e.target.value)}
                                    >
                                        {RCABINET_OPTIONS.map((o) => (
                                            <option key={o.value} value={o.value}>
                                                {o.label}
                                                {o.fee ? `（${o.fee.toLocaleString()}円）` : ""}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="text-sm text-gray-600">LINE公式アカウント</label>
                                    <select
                                        className="w-full border px-2 mt-1 bg-white text-sm h-7"
                                        value={tplLinePlan}
                                        onChange={(e) => setTplLinePlan(e.target.value)}
                                    >
                                        {LINE_PLAN_OPTIONS.map((o) => (
                                            <option key={o.value} value={o.value}>
                                                {o.label}
                                                {o.fee ? `（${o.fee.toLocaleString()}円）` : ""}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <label className="flex items-center gap-1 text-sm mt-3">
                                    <input
                                        type="checkbox"
                                        checked={tplRakurakuCoupon}
                                        onChange={(e) => setTplRakurakuCoupon(e.target.checked)}
                                    />
                                    らくらくーぽん（{RAKURAKU_COUPON_YEN.toLocaleString()}円）ON
                                </label>

                                <div>
                                    <label className="text-sm text-gray-600">ECマスターズクラブ</label>
                                    <select
                                        className="w-full border px-2 mt-1 bg-white text-sm h-7"
                                        value={tplEcmasters}
                                        onChange={(e) => setTplEcmasters(e.target.value)}
                                    >
                                        {ECMASTERS_OPTIONS.map((o) => (
                                            <option key={o.value} value={o.value}>
                                                {o.label}
                                                {o.fee ? `（${o.fee.toLocaleString()}円）` : ""}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="md:col-span-2 lg:col-span-4 border rounded bg-white p-2">
                                    <div className="mb-2 text-sm text-gray-600">全項目の一括適用</div>

                                    <div className="mt-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
                                        <div>
                                            <label className="text-sm text-gray-600">その他（合算）</label>
                                            <input
                                                type="number"
                                                className="w-full border px-2 mt-1 bg-white text-sm h-7"
                                                value={tplOtherCost}
                                                onChange={(e) => setTplOtherCost(e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    <div className="mt-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
                                        {FREE_ROWS.map((row) => (
                                            <div key={row.key} className="rounded border p-2 bg-gray-50">
                                                <div className="text-sm font-medium text-gray-700 mb-2">{row.label}</div>
                                                <div className="grid grid-cols-1 gap-2">
                                                    <div>
                                                        <label className="text-xs text-gray-500">名称</label>
                                                        <input
                                                            type="text"
                                                            className="w-full border px-2 mt-1 bg-white text-sm h-7"
                                                            value={tplFreeRows[row.key]?.name ?? ""}
                                                            onChange={(e) =>
                                                                setTplFreeRows((prev) => ({
                                                                    ...prev,
                                                                    [row.key]: {
                                                                        ...(prev[row.key] || { name: "", cost: "" }),
                                                                        name: e.target.value,
                                                                    },
                                                                }))
                                                            }
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="text-xs text-gray-500">費用（税込）</label>
                                                        <input
                                                            type="number"
                                                            className="w-full border px-2 mt-1 bg-white text-sm h-7"
                                                            value={tplFreeRows[row.key]?.cost ?? ""}
                                                            onChange={(e) =>
                                                                setTplFreeRows((prev) => ({
                                                                    ...prev,
                                                                    [row.key]: {
                                                                        ...(prev[row.key] || { name: "", cost: "" }),
                                                                        cost: e.target.value,
                                                                    },
                                                                }))
                                                            }
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <div className="mt-2 flex gap-2">
                                <button
                                    onClick={applyTemplateToRange}
                                    className={UI_BTN_PRIMARY}
                                    disabled={missingShopUrl}
                                >
                                    この複数月一括適用を範囲に適用
                                </button>
                            </div>

                            <div className="text-xs text-gray-500 mt-2">
                                ※月ごとに変更したい場合は、下の表で各月セルを直接変更してください（解約=OFF/なし）。<br />
                                ※複数月一括適用機能の中身は毎回リセットされます。下部の表の中身が固定費計算に適用されます
                            </div>
                        </>
                    )}
                </div>

                {/* 12ヶ月テーブル */}
                <div className="overflow-x-auto border rounded relative">
                    <table className={TABLE_CLASS}>
                        <thead className="bg-gray-100">
                            <tr>
                                <th className={`text-left p-1 border ${STICKY_TH}`}>科目</th>
                                {months.map((m) => (
                                    <th key={m.format("YYYY-MM")} className={`text-center ${TH_CELL}`}>
                                        {m.format("YYYY/MM")}
                                    </th>
                                ))}
                            </tr>
                        </thead>

                        <tbody>
                            {/* 楽天プラン */}
                            <tr>
                                <LeftCell>楽天出店プラン</LeftCell>
                                {monthKeyList.map((ym) => (
                                    <td key={ym} className={TD_CELL}>
                                        <select
                                            className={UI_SELECT_CELL}
                                            value={dataByMonth[ym]?.shop_plan ?? ""}
                                            onChange={(e) => setShopPlanMonth(ym, e.target.value)}
                                            disabled={missingShopUrl}
                                        >
                                            <option value="">未設定 / なし</option>
                                            {SHOP_PLANS_TAX_IN.map((p) => (
                                                <option key={p.label} value={p.label}>
                                                    {p.label}
                                                </option>
                                            ))}
                                        </select>
                                        <div className={UI_SUB_AMOUNT}>{(Number(dataByMonth[ym]?.shop_fee) || 0).toLocaleString()} 円</div>
                                    </td>
                                ))}
                            </tr>

                            {/* R-SNS */}
                            <tr>
                                <LeftCell>R-SNS（解約=OFF）</LeftCell>
                                {monthKeyList.map((ym) => {
                                    const enabled = Number(dataByMonth[ym]?.rsns) > 0;
                                    return (
                                        <td key={ym} className={TD_CELL}>
                                            <label className="flex items-center gap-1 text-[10px] leading-none">
                                                <input
                                                    type="checkbox"
                                                    checked={enabled}
                                                    onChange={(e) => toggleFixedAmountMonth(ym, "rsns", e.target.checked, RSNS_YEN)}
                                                    disabled={missingShopUrl}
                                                />
                                                ON
                                            </label>
                                            <div className={UI_SUB_AMOUNT}>{(enabled ? RSNS_YEN : 0).toLocaleString()} 円</div>
                                        </td>
                                    );
                                })}
                            </tr>

                            {/* RMS一括 */}
                            <tr>
                                <LeftCell>RMS商品一括編集機能（解約=OFF）</LeftCell>
                                {monthKeyList.map((ym) => {
                                    const enabled = Number(dataByMonth[ym]?.rms_batch_edit) > 0;
                                    return (
                                        <td key={ym} className={TD_CELL}>
                                            <label className="flex items-center gap-1 text-[10px] leading-none">
                                                <input
                                                    type="checkbox"
                                                    checked={enabled}
                                                    onChange={(e) => toggleFixedAmountMonth(ym, "rms_batch_edit", e.target.checked, RMS_BATCH_EDIT_YEN)}
                                                    disabled={missingShopUrl}
                                                />
                                                ON
                                            </label>
                                            <div className={UI_SUB_AMOUNT}>{(enabled ? RMS_BATCH_EDIT_YEN : 0).toLocaleString()} 円</div>
                                        </td>
                                    );
                                })}
                            </tr>

                            {/* R-Cabinet */}
                            <tr>
                                <LeftCell>R-Cabinet 容量追加（なし=OFF）</LeftCell>
                                {monthKeyList.map((ym) => (
                                    <td key={ym} className={TD_CELL}>
                                        <select className={UI_SELECT_CELL} value={detectRcabinetValue(ym)} onChange={(e) => setRcabinetMonth(ym, e.target.value)} disabled={missingShopUrl}>
                                            {RCABINET_OPTIONS.map((o) => (
                                                <option key={o.value} value={o.value}>
                                                    {o.label}
                                                </option>
                                            ))}
                                        </select>
                                        <div className={UI_SUB_AMOUNT}>{(Number(dataByMonth[ym]?.[FIELDS.RCABINET.cost]) || 0).toLocaleString()} 円</div>
                                    </td>
                                ))}
                            </tr>

                            {/* LINE */}
                            <tr>
                                <LeftCell>LINE公式アカウント（なし=OFF）</LeftCell>
                                {monthKeyList.map((ym) => (
                                    <td key={ym} className={TD_CELL}>
                                        <select className={UI_SELECT_CELL} value={detectLineValue(ym)} onChange={(e) => setLinePlanMonth(ym, e.target.value)} disabled={missingShopUrl}>
                                            {LINE_PLAN_OPTIONS.map((o) => (
                                                <option key={o.value} value={o.value}>
                                                    {o.label}
                                                </option>
                                            ))}
                                        </select>
                                        <div className={UI_SUB_AMOUNT}>{(Number(dataByMonth[ym]?.line_plan_cost) || 0).toLocaleString()} 円</div>
                                    </td>
                                ))}
                            </tr>

                            {/* らくらくーぽん */}
                            <tr>
                                <LeftCell>らくらくーぽん（解約=OFF）</LeftCell>
                                {monthKeyList.map((ym) => {
                                    const enabled = Number(dataByMonth[ym]?.[FIELDS.RAKURAKU.cost]) > 0;
                                    return (
                                        <td key={ym} className={TD_CELL}>
                                            <label className="flex items-center gap-1 text-[10px] leading-none">
                                                <input type="checkbox" checked={enabled} onChange={(e) => toggleRakurakuCouponMonth(ym, e.target.checked)} disabled={missingShopUrl} />
                                                ON
                                            </label>
                                            <div className={UI_SUB_AMOUNT}>{(enabled ? RAKURAKU_COUPON_YEN : 0).toLocaleString()} 円</div>
                                        </td>
                                    );
                                })}
                            </tr>

                            {/* ECマスターズ */}
                            <tr>
                                <LeftCell>ECマスターズクラブ（なし=OFF）</LeftCell>
                                {monthKeyList.map((ym) => (
                                    <td key={ym} className={TD_CELL}>
                                        <select className={UI_SELECT_CELL} value={detectEcmastersValue(ym)} onChange={(e) => setEcmastersMonth(ym, e.target.value)} disabled={missingShopUrl}>
                                            {ECMASTERS_OPTIONS.map((o) => (
                                                <option key={o.value} value={o.value}>
                                                    {o.label}
                                                </option>
                                            ))}
                                        </select>
                                        <div className={UI_SUB_AMOUNT}>{(Number(dataByMonth[ym]?.[FIELDS.ECMASTERS.cost]) || 0).toLocaleString()} 円</div>
                                    </td>
                                ))}
                            </tr>

                            {/* 自由入力 */}
                            {FREE_ROWS.map((r) => {
                                const map = (FIELDS as any)[r.key] as { name: string; cost: string };
                                const nameKey = map.name;
                                const costKey = map.cost;

                                const firstYm = monthKeyList[0] || "";
                                return (
                                    <tr key={r.key}>
                                        <LeftCell className="font-normal">
                                            <div className="flex items-center gap-2">
                                                <div className="font-medium whitespace-nowrap">{r.label}</div>

                                                <input
                                                    type="text"
                                                    className={UI_INPUT_TEXT_CELL}
                                                    placeholder={r.ex}
                                                    value={firstYm ? (dataByMonth[firstYm]?.[nameKey] ?? "") : ""}
                                                    onChange={(e) =>
                                                        applyToMonths(monthKeyList, (cur) => ({
                                                            ...cur,
                                                            [nameKey]: e.target.value,
                                                        }))
                                                    }
                                                    disabled={missingShopUrl}
                                                />
                                            </div>
                                        </LeftCell>

                                        {monthKeyList.map((ym) => (
                                            <td key={ym} className={TD_CELL}>
                                                <input
                                                    type="number"
                                                    className={UI_INPUT_NUM_CELL}
                                                    value={dataByMonth[ym]?.[costKey] ?? ""}
                                                    onChange={(e) => handleIntChange(ym, costKey, e.target.value)}
                                                    disabled={missingShopUrl}
                                                />
                                            </td>
                                        ))}
                                    </tr>
                                );
                            })}

                            {/* その他（合算） */}
                            <tr>
                                <LeftCell>その他（合算）</LeftCell>
                                {monthKeyList.map((ym) => (
                                    <td key={ym} className={TD_CELL}>
                                        <input
                                            type="number"
                                            className={UI_INPUT_NUM_CELL}
                                            value={dataByMonth[ym]?.[FIELDS.OTHER.cost] ?? ""}
                                            onChange={(e) => handleIntChange(ym, FIELDS.OTHER.cost, e.target.value)}
                                            disabled={missingShopUrl}
                                        />
                                    </td>
                                ))}
                            </tr>

                            {/* 合計 */}
                            <tr className={TD_CELL_GRAY}>
                                <LeftCell gray className="font-bold">
                                    合計（固定費合計）
                                </LeftCell>
                                {monthKeyList.map((ym) => (
                                    <td key={ym} className={`${TD_CELL_GRAY} text-right font-bold`}>
                                        {totalsByMonth[ym]?.toLocaleString()} 円
                                    </td>
                                ))}
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div className="text-xs text-gray-500 mt-2">
                    ※自由入力の登録例：一元管理ソフト、外部有料サービス、人件費、役員報酬、家賃、外注費、販管費、通信費等
                    <br />
                    ※「年度」は表示期間の開始年の意味。期首月が3月なら、2024年度＝2024/03〜2025/02 になります。
                </div>

                <FixedCostSupportInfo sectionId="fixed-cost-support-section" />
            </div>
        </div>
    );
}